import {Link, useNavigate} from 'react-router-dom';
import {Suspense, useEffect, useState} from "react";
import {toast, ToastContainer} from "react-toastify";
import Header from "../Header.jsx";
import Button from "../component-assets/Button.jsx";
import ApiRequester from "../ApiRequester.jsx";
import {
    campusToGuid,
    formatDateIso, formatPhone,
    formCheck,
    formFail,
    formSuccess, getAccessSettings,
    getToday,
    handleSubmitActions,
    iAmHigherAuthority, isBannerPresent, paginateData,
    setUserFilters,
    showModal,
    sortFunc,
    toggleCollapse
} from "../../libs.jsx";
import {Chart, registerables} from "chart.js";
import annotationPlugin from "chartjs-plugin-annotation";
import TableBody from "../component-assets/TableBody.jsx";
import TableHeaders from "../component-assets/TableHeaders.jsx";
import Navigation from "../Navigation.jsx";
import {Pagination} from "react-bootstrap";
import PaginationItems from "../component-assets/PaginationItems.jsx";
import PerPageModal from "../component-assets/PerPageModal.jsx";
import HourlyLineAndBarChart from "../charts/HourlyLineAndBarChart.jsx";
import LineTrendingChart from "../charts/LineTrendingChart.jsx";
import ProfileSkeleton from "../component-assets/skeletons/ProfileSkeleton.jsx";
import { getSession, setSession } from "../../services/session";

const UserProfile = (props) => {
    let dData, bData, startDate, endDate, eventHeaderData, noteHeaderData, logHeaderData;

    const navigate = useNavigate();

    dData = getSession();

    Chart.register(...registerables);
    Chart.register(annotationPlugin);

    eventHeaderData = [
        {label: "Event Start", key: "event_datetime_local", align: "center", sorts: false,
            link: {enabled: true, linkto: "/profiles/event", linkkey: "event_id", type: "datetime"}},
        {label: "Response (Min)", key: "response_time", align: "center", sorts: false},
        {label: "Device", key: ["description", "manufacture_uuid"], align: "center", sorts: false,
            link: {enabled: true, type: "stack", linkto: "/profiles/inovonics", linkkey: "device_id"}},
        {label: "Resident", key: "resident_name", align: "left", sorts: false,
            link: {enabled: true, type: "standard", linkto: "/profiles/resident", linkkey: "resident_id"}},
        {label: "Location", key: "area_name", align: "left", sorts: false,
            link: {enabled: true, type: "standard", linkto: "/profiles/area", linkkey: "area_id"}},
        {label: "Reason", key: "reason_code_description", align: "center", type: "indicator", sorts: false,
            indicator: {enabled: dData?.is_reason_editable, type: "length", key: "reason_edits",
                title: "This value has been edited"}}
    ];

    noteHeaderData = [
        {label: "Timestamp", key: "create_time", align: "center", type: "datetime-utc", sorts: false},
        {label: "Created By", key: "display_name", align: "center", sorts: false},
        {label: "Note", key: "note", align: "left", sorts: false},
    ]

    logHeaderData = [
        {label: "Timestamp", key: "create_time", align: "center", type: "datetime-utc", sorts: true},
        {label: "Log Event", key: "user_log_type", align: "center", sorts: true, filtered: true},
        {label: "Performed By", key: "display_name", align: "center", sorts: true, filtered: true}
    ];

    // Permission Variables
    const [editRights, setEditRights] = useState(iAmHigherAuthority(10, dData.role_weight));
    const [deleteRights, setDeleteRights] = useState(iAmHigherAuthority(10, dData.role_weight));
    const [read, setRead] = useState(true);
    // Main Page Variables
    const [userId, setUserId] = useState(props?.id);
    const [higherRole, setHigherRole] = useState(false);
    const [count, setCount] = useState(0);
    const [repCount, setRepCount] = useState(0);
    const [campusId, setCampusId] = useState(dData.acnt);
    const [tabItem, setTabItem] = useState("init");
    const [storedIndex, setStoredIndex] = useState(0);
    const [prenoteData, setPrenoteData] = useState([]);
    const [prenoteOptions, setPrenoteOptions] = useState([]);
    // Load Variables
    const [basicDataLoaded, setBasicDataLoaded] = useState(false); // Goes into Basic
    const [roleLoaded, setRoleLoaded] = useState(false); // Goes into Basic
    const [basicLoaded, setBasicLoaded] = useState(false);  // Handles Basic Tab Display
    const [hourDataLoaded, setHourDataLoaded] = useState(false); // Goes into Report
    const [weekDataLoaded, setWeekDataLoaded] = useState(false); // Goes into Report
    const [eventDataLoaded, setEventDataLoaded] = useState(false); // Goes into Report
    const [reasonDataLoaded, setReasonDataLoaded] = useState(false); // Goes into Report
    const [reportLoaded, setReportLoaded] = useState(false); // Handles Report Tab Display
    const [logDataLoaded, setLogDataLoaded] = useState(false); // Goes into Logs
    const [logsLoaded, setLogsLoaded] = useState(false); // Handles Logs Tab Display
    const [pageLoaded, setPageLoaded] = useState(false);
    // LineTrendingVariables
    const [lineData, setLineData] = useState([]);
    const [lineColors, setLineColors] = useState([]);
    const [lineLabels, setLineLabels] = useState([]);
    const [lineAxisLabels, setLineAxisLabels] = useState([]);
    // Basic Information Variables
    const [basicData, setBasicData] = useState({});
    const [roleData, setRoleData] = useState([]);
    const [roleOptions, setRoleOptions] = useState([]);
    const [userGroups, setUserGroups] = useState([]);
    const [oldUserGroups, setOldUserGroups] = useState([]);
    const [username, setUsername] = useState("");
    const [displayName, setDisplayName] = useState("");
    const [userRole, setUserRole] = useState("");
    const [userRoleId, setUserRoleId] = useState(1);
    const [cognitoId, setCognitoId] = useState("");
    const [title, setTitle] = useState("");
    const [email, setEmail] = useState("");
    const [phone, setPhone] = useState("");
    // Log Information Variables
    const [logOriginData, setLogOriginData] = useState([]);
    const [logActiveData, setLogActiveData] = useState([]);
    const [logTableRows, setLogTableRows] = useState([]);
    const [logHeaderFilters, setLogHeaderFilters] = useState([]);
    const [logSorter, setLogSorter] = useState({
        sortColumn: "create_time",
        sortOrder: "desc"
    });
    const [logPPRules, setLogPRules] = useState({
        start: 0,
        end: 1,
        beginDot: false,
        endDot: false,
        pageMin: 1,
        pageMax: 1,
        max: 1
    });
    const [logPage, setLogPage] = useState(1);
    const [tmpPerPage, setTmpPerPage] = useState(dData.perPage);
    const [perPage, setPerPage] = useState(dData.perPage);
    const [storePage, setStorePage] = useState(1);
    // Reporting Variables
    const [weekData, setWeekData] = useState({});
    const [hourData, setHourData] = useState({});
    const [reasonData, setReasonData] = useState({});
    const [reasonRows, setReasonRows] = useState([]);
    const [eventData, setEventData] = useState([]);
    const [eventTableRows, setEventTableRows] = useState([]);
    const [hourTimer, setHourTimer] = useState(100);
    const [weekTimer, setWeekTimer] = useState(100);
    const [noteTableRows, setNoteTableRows] = useState([]);
    const [noteTableLength, setNoteTableLength] = useState(0);

    startDate = formatDateIso(getToday(-28));
    endDate = formatDateIso(getToday());

    useEffect(() => {
        let interval = null;
        interval = setInterval(() => {
             setCount(count => count - 1);
        }, 1000);
        if (count <= 0){
            ApiRequester({reqEndpoint: "myfetch", payload: {user_filter: userId, campus_id: campusId},
                          procedure: "getuserprofile", reqType: "stored", "read-only": read}).then(data => {
                if (data){
                    setBasicData(data?.length > 0 ? data[0] : {});
                }
                setBasicDataLoaded(true);
            });
            ApiRequester({reqEndpoint: "myfetch", pKey: "campus_id", pVal: campusId, Tbl: "att_user_roles",
                                procedure: "fetchall", reqType: "direct", "read-only": true}).then(data => {
                setRoleData(data);
                setRoleLoaded(true);
            });
            ApiRequester({reqEndpoint: "myfetch", pKey: "user_id", pVal: userId,
                          procedure: "getuserlogs", reqType: "stored", "read-only": true}).then(data => {
                setLogOriginData(data);
                setLogDataLoaded(true);
            });
            setRead(true);
            setCount(() => 600);
        }
        return () => clearInterval(interval);
    }, [count]);

    useEffect(() => {
        if (![basicLoaded, reportLoaded, logsLoaded].includes(false)){
            setPageLoaded(true);
        }
    }, [basicLoaded, reportLoaded, logsLoaded]);

    useEffect(() => {
        if (![basicDataLoaded, roleLoaded].includes(false)){
            setBasicLoaded(true);
            if (tabItem === "init"){
                setTabItem("basic");
            }
        }
    }, [basicDataLoaded, roleLoaded]);

    useEffect(() => {
        if ([hourDataLoaded, weekDataLoaded, eventDataLoaded, reasonDataLoaded].every((item) => item === true)){
            setReportLoaded(true);
        }
    }, [hourDataLoaded, weekDataLoaded, eventDataLoaded, reasonDataLoaded]);

    useEffect(() => {
        if ([logDataLoaded].every((item) => item === true)){
            setLogsLoaded(true);
        }
    }, [logDataLoaded]);

    useEffect(() => {
        let tmp = [];
        if (roleData){
            roleData.sort((a, b) => sortFunc(a, b, "user_role_weight", "asc"));
            for (let i=0; i < roleData.length; i++){
                if (iAmHigherAuthority(roleData[i]?.user_role_weight, dData?.role_weight)){
                    tmp.push(<option key={`role-option-${i}`} value={roleData[i]?.user_role_id}>{roleData[i]?.user_role_name}</option>);
                }
            }
            setRoleOptions(tmp);
        }
    }, [roleData]);

    useEffect(() => {
        let interval = null;
        interval = setInterval(() => {
             setRepCount(repCount => repCount - 1);
        }, 1000);
        if (repCount <= 0){
            {dData?.event_notes_active && dData.event_notes_preform &&
                ApiRequester({reqEndpoint: "myfetch", pKey: "campus_id", pVal: dData.acnt, Tbl: "core_preform_event_notes",
                                procedure: "fetchbyparameter", reqType: "direct"}).then(data => {
                    setPrenoteData(data);
                });
            }
            ApiRequester({reqEndpoint: "get-mysql-report", rep_type: "dashboardhourchart",
                                campus_id: campusId, user_filter: userId.toString()}).then(data => {
                setHourData(data);
                setHourDataLoaded(true);
            });
            ApiRequester({reqEndpoint: "get-mysql-report", rep_type: "dashboardweekchart",
                                campus_id: campusId, user_filter: userId.toString()}).then(data => {
                setWeekData(data);
                setWeekDataLoaded(true);
            });
            ApiRequester({reqEndpoint: "get-mysql-report", rep_type: "eventReasonSummary",
                        campus_id: campusId, start_date: startDate, end_date: endDate,
                        device_category_filter: "", area_type_filter: "", reason_filter: "", user_filter: userId.toString(),
                        event_type_filter: "", resident_filter: "", device_filter: ""}).then(data => {
                setReasonData(() => data ? data : []);
                setReasonDataLoaded(true);
            });
            ApiRequester({reqEndpoint: "myfetch", payload: {campus_id: campusId, user_id: userId.toString()},
                                procedure: "getlimitedevents", reqType: "stored", "read-only": true}).then(data => {
                setEventData(data);
                setEventDataLoaded(true);
            });
            setRepCount(repCount => 1200);
        }
        return () => clearInterval(interval);
    }, [repCount]);

    useEffect(() => {
        if (Object.keys(basicData).length > 0){
            setUsername(() => basicData?.username ? basicData.username : "");
            setDisplayName(() => basicData?.display_name ? basicData.display_name : "");
            setUserRole(() => basicData?.user_role_name ? basicData.user_role_name : "");
            setUserRoleId(() => basicData?.user_role_id ? basicData.user_role_id : "");
            setEmail(() => basicData?.email ? basicData.email : "");
            setPhone(() => basicData?.phone ? formatPhone(basicData.phone) : "");
            setUserGroups(() => basicData?.groups ? basicData.groups : []);
            setOldUserGroups(() => basicData?.groups ? basicData.groups : []);
            setCognitoId(() => basicData?.cognito_id ? basicData.cognito_id : "");
            setTitle(() => basicData?.title ? basicData?.title : "");
            setHigherRole(() => basicData?.user_role_weight ? iAmHigherAuthority(basicData?.user_role_weight, dData.role_weight) : false);
            if (basicData?.user_role_weight > 80){
                document.getElementById("passwordR").pattern = "^(?!123456$)[ -~]{6,}$";
            }else{
                document.getElementById("passwordR").pattern = "[ -~]{6,}$";
            }
        }
    }, [basicData]);

    useEffect(() => {
        // Performs realtime search of data as location types.
        let tmpData, input, pRules;

        tmpData = logOriginData.filter(item => {
            return Object.keys(logHeaderFilters).every(key => {
                if (!logHeaderFilters[key] || logHeaderFilters[key].length === 0) return true;
                return logHeaderFilters[key].includes(String(item[key]));
            });
        });

        pRules = paginateData(tmpData, logPage, perPage);
        if (input && logPage > pRules.pageMax){
            setLogPage(pRules.pageMax);
        }else if (!input){
            setLogPage(storePage);
        }
        setLogPRules(pRules);
        setLogActiveData(tmpData);
    }, [logOriginData, logHeaderFilters]);

    useEffect(() => {
        let pRules = paginateData(logActiveData, logPage, perPage);
        setLogPRules(pRules);
        if (logActiveData.length > 0) {
            logActiveData.sort((a, b) => sortFunc(a, b, logSorter.sortColumn, logSorter.sortOrder));
        }
        setLogTableRows(<TableBody start={pRules.start} end={pRules.end} tableData={logActiveData} checkbox={false}
                                writeAccess={false} unk={"l-"} addlEditKey={""} addlEditCheck={false}
                                actionAppearance={() => {}}
                                tableHeaders={logHeaderData} editModal={() => {}} parentComponent={"User"} dData={dData}
                                modal={false} checkboxlist={[]} setCheckboxId={() => {}}
                                checkidkey={""} tzOffset={parseInt(dData?.campusData[campusId]?.tz_offset ? dData?.campusData[campusId]?.tz_offset : 0)} />);
    }, [logActiveData, logSorter, logPage, perPage]);

    useEffect(() => {
        let tmp = [];
        if (prenoteData.length > 0){
            for (let i=0; i < prenoteData.length; i++){
                tmp.push(
                    <option key={`prenote-option-${i}`} value={prenoteData[i]?.preform_note}>{prenoteData[i]?.preform_note}</option>
                );
            }
        }
        if (dData?.event_notes_freeform){
            tmp.push(
                <option key={`prenote-option-00`} value={"0"}>Other (freeform note)</option>
            );
        }
        setPrenoteOptions(() => tmp);
    }, [prenoteData]);

    useEffect(() => {
        if (dData?.event_notes_active){
            setEventTableRows(<TableBody start={0} end={eventData.length} tableData={eventData} checkbox={false}
                                         writeAccess={true} unk={"user-"} modalType={"add"} modalLabel={"Add Note"}
                                         actionAppearance={() => {}} parentComponent={"User"}
                                         tableHeaders={eventHeaderData} modal={true} editModal={addNoteModal}
                                         dData={dData} checkboxlist={[]} editModalBadge={"notes"}
                                         setCheckboxId={() => {}} checkidkey={"user_id"} />);
        }else{
            setEventTableRows(<TableBody start={0} end={eventData.length} tableData={eventData} checkbox={false}
                                         writeAccess={true} unk={"user-"} modalType={"add"} modalLabel={"Add Note"}
                                         actionAppearance={() => {}} parentComponent={"User"}
                                         tableHeaders={eventHeaderData} modal={false} editModal={() => {}}
                                         dData={dData} checkboxlist={[]} editModalBadge={"notes"}
                                         setCheckboxId={() => {}} checkidkey={"user_id"} />);
        }
    }, [eventData]);

    useEffect(() => {
        if (weekData){
            setLineColors(["#0081C7"]);
            setLineLabels(["Total Alarms"]);
            setLineData([weekData?.events?.length > 0 ? weekData.events : []]);
            setLineAxisLabels(weekData?.labels?.length > 0 ? weekData.labels : []);
        }
    }, [weekData]);

    function basicActionHandling(){
        document.getElementById("display-static").classList.toggle("d-none");
        document.getElementById("display-edit").classList.toggle("d-none");
        document.getElementById("userrole-static").classList.toggle("d-none");
        document.getElementById("userrole-edit").classList.toggle("d-none");
        document.getElementById("email-static")?.classList?.toggle("d-none");
        document.getElementById("email-edit")?.classList?.toggle("d-none");
        document.getElementById("phone-static")?.classList?.toggle("d-none");
        document.getElementById("phone-edit")?.classList?.toggle("d-none");
        document.getElementById("title-static")?.classList?.toggle("d-none");
        document.getElementById("title-edit")?.classList?.toggle("d-none");
        if (document.getElementById("edit-basic-btn").innerText === "Edit"){
            document.getElementById("edit-basic-btn").innerText = "Cancel";
            document.getElementById("save-basic-btn").classList.toggle("d-none");
            document.getElementById("display-edit").value = basicData?.display_name;
            document.getElementById("userrole-edit").value = basicData?.user_role_id;
            document.getElementById("email-edit").value = basicData?.email;
            document.getElementById("email-edit").required = ![6].includes(userRoleId);
            document.getElementById("phone-edit").value = basicData?.phone;
            
            // Handle title dropdown
            const titleEdit = document.getElementById("title-edit");
            const customTitleInput = document.getElementById("title-custom-edit");
            const standardTitles = ["Executive Director", "Maintenance Director", "Wellness Director", "Director of Nursing", "Nurse", "Caregiver"];
            
            if (title && standardTitles.includes(title)) {
                titleEdit.value = title;
                customTitleInput.classList.add("d-none");
            } else if (title) {
                titleEdit.value = "Other";
                customTitleInput.value = title;
                customTitleInput.classList.remove("d-none");
            } else {
                titleEdit.value = "";
                customTitleInput.classList.add("d-none");
            }
        }else{
            document.getElementById("edit-basic-btn").innerText = "Edit";
            document.getElementById("save-basic-btn").classList.toggle("d-none");
        }
        formCheck("basic-form", "save-basic-btn");
    }

    function basicSubmit(){
        let dName, roleId, newemail, payload, pAccess, groupIdList = [], oldPayload, newphone, newTitle;
        if (editRights && higherRole){
            handleSubmitActions("save-basic-btn", "load-basic-btn");
            dName = document.getElementById("display-edit").value;
            newemail = document.getElementById("email-edit").value;
            roleId = document.getElementById("userrole-edit").value;
            newphone = document.getElementById("phone-edit").value;
            newphone = newphone.replaceAll(" ", "").replaceAll("-", "");
            const titleValue = document.getElementById("title-edit").value;
            if (titleValue === "Other") {
                newTitle = document.getElementById("title-custom-edit").value;
            } else {
                newTitle = titleValue;
            }
            console.log(title)
            pAccess = getAccessSettings(parseInt(roleId));
            for (let i=0; i < userGroups.length; i++){
                groupIdList.push(userGroups[i].group_id);
            }
            if (dData?.campusData[dData?.acnt?.toString()]?.is_cloud) {
                payload = [{action: "update", user_id: userId, display_name: dName, email: email.toLowerCase(),
                        user_role_id: parseInt(roleId), group_ids: groupIdList, username: username, campus_id: campusId,
                        cognito_id: cognitoId, modified_by: dData.user, phone: newphone, title: newTitle,
                    oldPayload: {user_id: userId, display_name: displayName, email: email, user_role_id: userRoleId,
                            group_ids: oldUserGroups, username: username, cognito_id: cognitoId, title: title,
                            phone: basicData?.phone ? basicData.phone : ""}}];
                ApiRequester({reqEndpoint: "user-manage-2", Payload: payload, ClientId: dData.sessionId,
                                    User: dData.user}).then(data => {
                    if (data[0]){
                        formSuccess("User updated.", "save-basic-btn", "load-basic-btn", "edit-basic-btn");
                        setTitle(newTitle);
                        setRead(false);
                        setCount(0);
                        if (username === dData?.username){
                            dData.email = email.toLowerCase();
                            dData.display = dName;
                            dData.groups = groupIdList;
                            dData.title = newTitle;
                            setSession(dData);
                        }
                    }
                    else{
                        formFail("Error during save, error code: " + data[1], "editSubmit", "loadEditSubmit");
                    }
                });
            }else{
                payload = [{Action: "update", UserId: userId, DisplayName: dName, UserEmail: newemail.toLowerCase(),
                        UserRole: parseInt(roleId), CampusGuid: campusToGuid(dData, campusId),
                        PortalAccess: pAccess, GroupIds: groupIdList, Username: username, CampusId: campusId,
                        Migrated: true, CognitoId: cognitoId, ClientId: dData.sessionId, PhoneNumber: newphone, title: newTitle}];
                oldPayload = [{user_id: userId, display_name: displayName, email: email, user_role_id: userRoleId,
                               username: username, groups: oldUserGroups, campus_id: campusId, title: title,
                               phone: basicData?.phone ? basicData.phone : ""}];
                ApiRequester({reqEndpoint: "user-manage", Payload: payload, OldPayload: oldPayload,
                                    User: dData.user}).then(data => {
                    if (data[0]){
                        setTitle(newTitle);
                        setRead(false);
                        setCount(0);
                        formSuccess("Basic information saved.", "save-basic-btn", "load-basic-btn", "edit-basic-btn")
                    }
                    else{
                        formFail(`Error during save, error code: ${data && data?.length > 1 ? data[1] : ""}`,
                            "save-basic-btn", "load-basic-btn");
                    }
                });
            }
        }else{
            toast.warn("You do not have permissions to perform this action.");
        }
    }

    function addNoteModal(rowIndex){
        let dataIndex;
        dataIndex = parseInt(rowIndex);
        setStoredIndex(dataIndex);
        document.getElementById("note").value = "";
        setNoteTableLength(eventData[dataIndex].notes.length);
        setNoteTableRows(<TableBody start={0} end={eventData[dataIndex].notes.length}
                                    tableData={eventData[dataIndex].notes} checkbox={false}
                                     writeAccess={false} unk={"n-"} actionAppearance={() => {}}
                                     tableHeaders={noteHeaderData} modal={false}
                                     parentComponent={"Event"} dData={dData} checkboxlist={[]}
                                     setCheckboxId={() => {}} checkidkey={"note_id"}
                                     tzOffset={parseInt(dData?.campusData[campusId]?.tz_offset ? dData?.campusData[campusId]?.tz_offset : 0)}/>);
        formCheck("addNoteForm", "addNoteSubmit");
        showModal("addnotemodal");
    }

    function addNoteSubmit(){
        let payload, eventId, note;
        handleSubmitActions("addNoteSubmit", "loadAddNoteSubmit");
        eventId = eventData[storedIndex]?.event_id;
        note = document.getElementById("note").value;
        if (note === "0"){
            note = document.getElementById("note-freeform").value;
        }
        payload = [{event_id: eventId, campus_id: dData.acnt, note: note, modified_by: dData?.user}];
        ApiRequester({reqEndpoint: "mysave", Tbl: "core_event_notes", Payload: payload, urlType: "PUT",
                            User: dData.user, PKey: "note_id", Condition: "primary", CampusId: campusId,
                            ClientId: dData.sessionId}).then(data => {
            if (data[0] === true){
                formSuccess(`Note added successfully.`, "addNoteSubmit", "loadAddNoteSubmit", "cancelAddNoteBtn", "addNoteForm");
                setRepCount(0);
                document.getElementById("submitBtn").click()
            }
            else{
                formFail(`Error during add, error code: ${data[1]}`, "addNoteSubmit", "loadAddNoteSubmit");
            }
        });
    }

    function passSubmit(){
        let password, msg, payload;
        if (editRights && higherRole){
            handleSubmitActions("passSubmit", "loadPassSubmit");
            password = document.getElementById("passwordR").value;
            if (dData?.campusData[dData?.acnt?.toString()]?.is_cloud) {
                payload = [{action: "reset", campus_id: campusId, username: username, password: password, user_id: userId,
                            modified_by: dData.user}];
                ApiRequester({reqEndpoint: "user-manage-2", Payload: payload, ClientId: dData.sessionId,
                                    User: dData.user}).then(data => {
                    if (data[0] === true){
                        setRead(false);
                        formSuccess("Password reset successfully.", "passSubmit", "loadPassSubmit", "cancelPassBtn", "passForm");
                        setCount(0);
                    }
                    else{
                        switch (data[1]) {
                            case "8":
                                msg = "User not found error, request not processed";
                                break;
                            default:
                                msg = `Unknown failure, request not processed. Error code ${data[1]}`;
                                break;
                        }
                        formFail(msg, "passSubmit", "loadPassSubmit");
                    }
                });
            }else{
                payload = [{Action: "reset", CampusId: campusId, Username: username, UserPass: password, Migrated: true}];
                ApiRequester({reqEndpoint: "user-manage", Payload: payload}).then(data => {
                    if (data[0] === true){
                        setRead(false);
                        formSuccess("Password reset successfully.", "passSubmit", "loadPassSubmit", "cancelPassBtn", "passForm");
                        setCount(0);
                    }
                    else{
                        if (data[1] === "3"){
                            msg = "Current password does not match records.";
                        }
                        else if (data[1] === "4"){
                            msg = "User not authorized.";
                        }
                        else if (data[1] === "5"){
                            msg = "Error encountered authenticating user. Please contact support for assistance if error persists.";
                        }
                        else if (data[1] === "6"){
                            msg = "New password failed to be set, check password and try again. Contact support if failure persists.";
                        }
                        else{
                            msg = "Unknown failure, request not processed.";
                        }
                        formFail(msg, "passSubmit", "loadPassSubmit");
                    }
                });
            }
        }else{
            toast.warn("You do not have permissions to perform this action.");
        }
    }

    function deletePerform(){
        let payload;
        if (deleteRights && higherRole){
            handleSubmitActions("deleteConfirmSubmit", "loadDeleteConfirmSubmit");
            if (dData?.campusData[dData?.acnt?.toString()]?.is_cloud) {
                payload = [{action: "remove", user_id: userId, username: username, campus_id: campusId,
                            modified_by: dData?.user, cognito_id: cognitoId}];
                ApiRequester({reqEndpoint: "user-manage-2", Payload: payload, User: dData.user,
                              ClientId: dData.sessionId}).then(data => {
                    if (data[0]){
                        document.getElementById("deleteConfirmCancelBtn").click();
                        navigate("/users/usermanagement", {state: {message: {success: `${displayName} has been deleted.`}}});
                    }
                    else{
                        formFail(`Error during delete, error code: ${data[1]}`,
                            "deleteConfirmSubmit", "loadDeleteConfirmSubmit");
                    }
                });
            }else{
                payload = [{Action: "remove", UserId: userId, Username: username, CampusId: campusId,
                            RequestedBy: dData?.username, Migrated: true, CognitoId: cognitoId, ClientId: dData.sessionId}];
                ApiRequester({reqEndpoint: "user-manage", Payload: payload}).then(data => {
                    if (data[0]){
                        document.getElementById("deleteConfirmCancelBtn").click();
                        navigate("/users/usermanagement", {state: {message: {success: `${displayName} has been deleted.`}}});
                    }
                    else{
                        formFail(`Error during delete, error code: ${data[1]}`,
                            "deleteConfirmSubmit", "loadDeleteConfirmSubmit");
                    }
                });
            }
        }else{
            toast.warn("You do not have permissions to perform this action.");
        }
    }

    function filterSubmit() {
        handleSubmitActions("filterSubmit", "loadFilterSubmit");
        setPerPage(tmpPerPage);
        dData.perPage = tmpPerPage;
        setLogPage(1);
        setSession(dData);
        formSuccess("Page parameters stored to session.", "filterSubmit",
            "loadFilterSubmit", "cancelFilterBtn");
        setCount(0);
    }

    const eventTableHeaders = <TableHeaders checkbox={false} headerData={eventHeaderData} checkAll={() => {}}
                                            writeAccess={true} sortOrder={""}
                                            checkboxHeaderId={"uCheckHead"} sortColumn={""} sorter={{}}
                                            setSorter={() => {}} modal={dData?.event_notes_active} modal_name={"Notes"} checkboxlist={[]}
                                            actionAppearance={() => {}}/>
    const notesTableHeaders = <TableHeaders checkbox={false} headerData={noteHeaderData}
                                            checkAll={() => {}} writeAccess={false} sortOrder={"asc"}
                                            sortColumn={"create_time"} sorter={{}}
                                            setSorter={() => {}} modal={false} checkboxlist={[]}
                                            actionAppearance={() => {}}/>
    const logTableHeaders = <TableHeaders checkbox={false} headerData={logHeaderData} checkAll={() => {}}
                                          writeAccess={false} sortOrder={logSorter.sortOrder} modal_name={""}
                                          originData={logOriginData} headerFiltersOn={true}
                                          sortColumn={logSorter.sortColumn} sorter={logSorter} setSorter={setLogSorter}
                                          modal={false} checkboxlist={[]} actionAppearance={() => {}}
                                          headerFilters={logHeaderFilters} setHeaderFilters={setLogHeaderFilters} setPage={setLogPage}/>

    const headerContent = (
        <div className={"d-flex justify-content-between"}>
            <div></div>
            {pageLoaded && higherRole &&
                <div>
                    {editRights &&
                        <Button text={"Reset Password"} class={"btn btn-primary me-3"} id={"passBtn"}
                             style={{marginTop: "1.1rem"}} onClick={() => {
                                 formCheck("passForm", "passSubmit");
                                 showModal("passmodal");
                            }} />
                    }
                    {deleteRights &&
                        <Button text={"Delete"} class={"btn btn-danger"} id={"deleteBtn"}
                            style={{marginTop: "1.1rem"}} onClick={() => {
                                 showModal("deleteconfirmmodal");
                            }} />
                    }
                </div>
            }
        </div>
    );

    return (
        <>
            <Navigation />
            <div className={"main-content"}>
                <Header preTitle={"User Profile"} Title={displayName} content={headerContent} subTitle={title}/>
                <div className={"container-fluid"}>
                    <div className={"col"}>
                        <ul className={"nav nav-tabs nav-overflow header-tabs"}>
                            <li key={"set-basic"} className={"nav-item"}>
                                <Button text={"Basic Info"} spinner={!basicLoaded}
                                        class={tabItem === "basic" ? "nav-link active" : "nav-link"}
                                        onClick={() => setTabItem("basic")}/>
                            </li>
                            <li key={"set-reporting"} className={"nav-item"}>
                                <Button text={"Reporting"} spinner={!reportLoaded}
                                        class={tabItem === "reporting" ? "nav-link active" : "nav-link"}
                                        onClick={() => setTabItem("reporting")}/>
                            </li>
                            {iAmHigherAuthority(80, dData.role_weight)
                                ? <li key={"set-logs"} className={"nav-item"}>
                                    <Button text={"User Logs"} spinner={!logsLoaded}
                                            class={tabItem === "logs" ? "nav-link active" : "nav-link"}
                                            onClick={() => setTabItem("logs")}/>
                                </li>
                                : <></>
                            }
                        </ul>
                    </div>
                    <div className={tabItem === "init" ? "d-block" : "d-none"}>
                        <ProfileSkeleton />
                    </div>
                    <div className={tabItem === "basic" ? "d-block" : "d-none"}>
                        <div className={"card"} style={{minWidth: "550px"}}>
                            <div className={"card-header d-flex justify-content-between"}>
                                <div>
                                    <h3 className={"card-header-title d-inline-block"}>Basic Information</h3>
                                </div>
                                <div>
                                    <i className={"fe fe-minus as-btn collapse-toggle p-2"} id={"basic-card-btn"}
                                       onClick={() => toggleCollapse("basic-card-btn", "basic-card")}></i>
                                </div>
                            </div>
                            <div className={"card-body collapse collapse-content show"} id={"basic-card"}>
                                <form className={"d-inline align-top form-control-plaintext was-validated"}
                                      id={"basic-form"}>
                                    <div className={"mx-5 mt-2 align-top d-inline-block"} id={"basic-col1-div"}>
                                        <div className={"d-flex"}>
                                            <p style={{minWidth: "125px"}}>Username:</p>
                                            <p className={"ms-4"} id={"username-static"}>{username}</p>
                                        </div>
                                        <div className={"d-flex"}>
                                            <p style={{minWidth: "125px"}}>Display Name:</p>
                                            <p className={"ms-4"}
                                               id={"display-static"}>{displayName}</p>
                                            <input className={"ms-4 d-none form-control form-control-sm"}
                                                   style={{height: "30px", width: "256px"}} id={"display-edit"}
                                                   defaultValue={displayName} pattern={"[a-zA-Z0-9 ]+"}/>
                                        </div>
                                        <div className={"d-flex"}>
                                            <p style={{minWidth: "125px"}}>Email:</p>
                                            <p className={"ms-4"} id={"email-static"}>{email}</p>
                                            <input className={"ms-4 d-none form-control form-control-sm"}
                                                   style={{height: "30px", width: "256px"}} id={"email-edit"}
                                                   defaultValue={email}
                                                   pattern={"[A-Za-z0-9._%+\\-]+@[A-Za-z0-9.\\-]+\\.[A-Za-z]{2,}$"}/>
                                        </div>
                                        <div className={"d-flex"}>
                                            <p style={{minWidth: "125px"}}>Phone:</p>
                                            <p className={"ms-4"} id={"phone-static"}>{phone}</p>
                                            <input className={"ms-4 d-none form-control form-control-sm"}
                                                   style={{height: "30px", width: "256px"}} id={"phone-edit"}
                                                   defaultValue={phone}
                                                   pattern={"[0-9]{3}-?[0-9]{3}-?[0-9]{4}"}/>
                                        </div>
                                    </div>
                                    <div className={"mx-5 mt-2 d-inline-block align-top"} id={"basic-col2-div"}>
                                        <div className={"d-flex"}>
                                            <p style={{minWidth: "75px"}}>Role:</p>
                                            <p className={"ms-4"} id={"userrole-static"}>{userRole}</p>
                                            <select className={"ms-4 d-none form-select form-select-sm mb-3"}
                                                    style={{width: "256px"}}
                                                    required={true} id={"userrole-edit"} defaultValue={userRoleId}
                                                    onChange={(e) => {
                                                        document.getElementById("email-edit").required = !["caregiver", "6"].includes(e?.target?.value?.toString());
                                                        if (["1", "2"].includes(e.target.value)) {
                                                            document.getElementById("passwordR").pattern = "^(?!123456$)[ -~]{6,}$";
                                                        } else {
                                                            document.getElementById("passwordR").pattern = "[ -~]{6,}$";
                                                        }
                                                        formCheck("basic-form", "save-basic-btn");
                                                    }}>
                                                {roleOptions}
                                            </select>
                                        </div>
                                        <div className={"d-flex"}>
                                            <p style={{minWidth: "75px"}}>Job Title:</p>
                                            <p className={"ms-4"} id={"title-static"}>{title}</p>
                                            <div className={"ms-4 d-flex flex-column"}>
                                                <select className={"d-none form-select form-select-sm mb-3"}
                                                        style={{width: "256px"}}
                                                        id={"title-edit"} defaultValue={title}
                                                        onChange={(e) => {
                                                            const isOther = e.target.value === "Other";
                                                            const customInput = document.getElementById("title-custom-edit");
                                                            if (customInput) {
                                                                customInput.classList.toggle("d-none", !isOther);
                                                                customInput.required = isOther;
                                                            }
                                                            formCheck("basic-form", "save-basic-btn");
                                                        }}>
                                                    <option value={""}></option>
                                                    <option value={"Executive Director"}>Executive Director</option>
                                                    <option value={"Maintenance Director"}>Maintenance Director</option>
                                                    <option value={"Wellness Director"}>Wellness Director</option>
                                                    <option value={"Director of Nursing"}>Maintenance Director</option>
                                                    <option value={"Nurse"}>Nurse</option>
                                                    <option value={"Caregiver"}>Caregiver</option>
                                                    <option value={"Other"}>Other</option>
                                                </select>
                                                <input className={"d-none form-control form-control-sm"}
                                                       onKeyUp={() => formCheck("basic-form", "save-basic-btn")}
                                                       onChange={(e) => {formCheck("basic-form", "save-basic-btn");}}
                                                       style={{height: "30px", width: "256px"}} id={"title-custom-edit"}
                                                       defaultValue={""} placeholder={"Enter custom title..."}/>
                                            </div>
                                        </div>
                                        <div className={"d-flex"}>
                                            <ul className={"ps-0 d-inline-block align-top mb-0"}
                                                style={{listStyle: "none"}}>
                                                <li><p style={{minWidth: "75px"}} className={"me-4"}>Groups:</p></li>
                                            </ul>
                                            <ul className={"ps-0 d-inline-block mb-0"} style={{listStyle: "none"}}>
                                                {userGroups.map((group, i) => {
                                                    return <li
                                                        key={`user-group-name-item-${i}`}>{group?.group_name}</li>
                                                })}
                                            </ul>
                                        </div>
                                    </div>
                                </form>
                                {pageLoaded && editRights && higherRole &&
                                    <>
                                        <Button text={"Edit"} class={"btn btn-primary float-end"} id={"edit-basic-btn"}
                                                disabled={!editRights} onClick={() => {
                                            if (editRights) {
                                                basicActionHandling()
                                            }
                                        }}/>
                                        <Button text={"Save"} class={"btn btn-primary float-end me-3 d-none"}
                                                id={"save-basic-btn"}
                                                disabled={!editRights} onClick={() => {
                                            if (editRights) {
                                                basicSubmit()
                                            }
                                        }}/>
                                        <Button type={"button"} class={"btn btn-primary float-end mx-3 d-none"}
                                                disabled={true}
                                                spinner={true} text={""} id={"load-basic-btn"}/>
                                    </>
                                }
                            </div>
                        </div>
                    </div>
                    <div className={tabItem === "reporting" ? "d-block" : "d-none"}>
                        <div className={"card"} style={{minWidth: "500px"}}>
                            <div className={"card-header d-flex justify-content-between"}>
                                <div>
                                    <h3 className={"card-header-title d-inline-block"} style={{minWidth: "80px"}}>
                                        Reporting
                                    </h3>
                                    <div className={"d-none"} style={{display: "inline-block"}}
                                         id={"reporting-header-data"}>
                                        <h3 className={"ms-6 my-0 d-inline-block text-success"}>{!isNaN(hourData?.avgValue) ? hourData.avgValue : "?"} Alert/Week</h3>
                                        <h3 className={"mx-4 my-0 d-inline-block text-success"}>{!isNaN(weekData?.avgValue) ? weekData.avgValue : "?"} Avg/Resp</h3>
                                    </div>
                                </div>
                                <div>
                                    <i className={"fe fe-minus as-btn collapse-toggle p-2"} id={"reporting-card-btn"}
                                       onClick={() => {
                                           toggleCollapse("reporting-card-btn", "reporting-card")
                                           document.getElementById("reporting-header-data").classList.toggle("d-none");
                                       }}></i>
                                </div>
                            </div>
                            <div className={"card-body collapse collapse-content show"} id={"reporting-card"}>
                                <div className={"row"}>
                                    <Link style={{width: "auto", marginLeft: "auto"}} to={"/reports/alerts/run"} onClick={() => {
                                        setUserFilters(dData, userId, basicData?.username);
                                    }}>
                                        <Button text={"Run Detailed Report"} class={"btn btn-primary mb-4 float-end"}
                                                icon={true} iconClass={"fe fe-arrow-right-circle float-end ms-3"}/>
                                    </Link>
                                </div>
                                <div className={"row"}>
                                    <div className={"chart-container w-50 text-end align-top d-inline-block"}>
                                        {hourDataLoaded
                                            ?
                                            <HourlyLineAndBarChart
                                                style={{maxHeight: "425px"}}
                                                labels={hourData?.labels ? hourData.labels : ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9", "10", "11", "12",
                                                                "13", "14", "15", "16", "17", "18", "19", "20", "21", "22", "23"]}
                                                x_axis={true}
                                                x_axis_title={"Time of the Day"}
                                                bar_axis={true}
                                                bar_axis_title={"Total Alarms"}
                                                bar_color={"#0081C7"}
                                                line_axis={true}
                                                line_axis_title={"Average Response Time"}
                                                line_color={"#BE4D25"}
                                                bar_data={hourData?.events ? hourData.events : []}
                                                line_data={hourData?.responses ? hourData.responses : []}
                                                bar_annotation={true}
                                                line_annotation={true}
                                                forceAspect={true}
                                                bar_annotation_value={hourData?.avgValue ? hourData.avgValue : 0}
                                                line_annotation_value={hourData?.avgResp ? hourData.avgResp : 0} />
                                            :
                                            (<div className={"text-center"}>
                                                <Button text={""} class={"border-0 bg-transparent btn"}
                                                        spinner={true}></Button>
                                            </div>)
                                        }
                                    </div>
                                    <div className={"chart-container w-50 text-center align-top d-inline-block"}>
                                        {weekDataLoaded
                                            ?
                                            <LineTrendingChart
                                                style={{maxHeight: "425px"}}
                                                x_axis={true}
                                                x_axis_title={"Weeks"}
                                                line_axis={true}
                                                line_axis_title={"Total Alarms"}
                                                lines={1}
                                                line_color={lineColors}
                                                line_labels={lineLabels}
                                                line_data={lineData}
                                                forceAspect={true}
                                                labels={lineAxisLabels} />
                                            :
                                            (<div className={"text-center"}>
                                                <Button text={""} class={"border-0 bg-transparent btn"}
                                                        spinner={true}></Button>
                                            </div>)
                                        }
                                    </div>
                                </div>
                                <div className={"row mt-5"}>
                                    <div className={"w-50 px-4"}>
                                        <table className={"table table-sm table-white table-hover"}>
                                            <thead>
                                            <tr>
                                                <th className={"table table-header w-auto py-1"} colSpan={"4"}>
                                                    Event Reason Breakdown
                                                </th>
                                            </tr>
                                            <tr>
                                                <th className={"table table-header w-auto py-1"}>
                                                    Reason
                                                </th>
                                                <th className={"table table-header w-auto py-1"}>
                                                    Total
                                                </th>
                                                <th className={"table table-header w-auto py-1"}>
                                                    % Total Volume
                                                </th>
                                                <th className={"table table-header w-auto py-1"}>
                                                    Average Response (min)
                                                </th>
                                            </tr>
                                            </thead>
                                            <tbody>
                                            <tr>
                                                <td className={"ps-1 fw-bold"}>Total Events</td>
                                                <td className={"ps-1 fw-bold text-center"}>{reasonData?.count ? reasonData.count : 0}</td>
                                                <td className={"ps-1 text-right"}></td>
                                                <td className={"ps-1 fw-bold text-center"}>{reasonData?.avg_response_time ? reasonData.avg_response_time : 0.0}</td>
                                            </tr>
                                            {reasonRows}
                                            </tbody>
                                        </table>
                                    </div>
                                    <div className={"w-50"} style={{maxHeight: "400px", overflowY: "auto"}}>
                                        <table className={"table table-sm table-white table-hover d-sm-table"}>
                                            <thead className={"sticky-table-head"} style={{top: 0, zIndex: 1}}>
                                            {eventTableHeaders}
                                            </thead>
                                            <tbody>
                                            {eventTableRows}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className={tabItem === "logs" ? "d-block" : "d-none"}>
                        <div className={"card"} style={{minWidth: "500px"}}>
                            <div className={"card-header d-flex justify-content-between"}>
                                <div>
                                    <h3 className={"card-header-title d-inline-block"}
                                        style={{minWidth: "80px"}}>Device Logs</h3>
                                </div>
                                <div>
                                    <i className={"fe fe-minus as-btn collapse-toggle p-2"} id={"wellness-card-btn"}
                                       onClick={() => toggleCollapse("logs-card-btn", "logs-card")}></i>
                                </div>
                            </div>
                            <div className={"card-body collapse collapse-content show"} id={"logs-card"}>
                                <table className={"table table-sm table-white table-hover"}>
                                    <thead className={isBannerPresent() ? "sticky-table-headX" : "sticky-table-head"}>
                                    {logTableHeaders}
                                    </thead>
                                    <tbody id={"tBody"}>
                                    {logTableRows}
                                    </tbody>
                                </table>
                                <div className={"text-center"}>
                                    <Pagination>
                                        <PaginationItems setPage={setLogPage} page={logPage}
                                                         beginDot={logPPRules.beginDot}
                                                         endDot={logPPRules.endDot} pageMin={logPPRules.pageMin}
                                                         pageMax={logPPRules.pageMax}
                                                         max={logPPRules.max}/>
                                    </Pagination>
                                    <Button class={"btn btn-secondary fs-5 ms-4"} text={perPage}
                                            onClick={() => showModal("filtermodal")}/>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className={"modal fade"} id={"passmodal"} data-bs-backdrop={"static"} data-bs-keyboard={"false"}
                     tabIndex={-1} aria-labelledby={"staticBackdropLabel"} aria-hidden={"true"}>
                    <div className={"modal-dialog"}>
                        <div className={"modal-content"}>
                            <div className={"modal-header bg-pal"}>
                                <h2 className={"modal-title"} id={"staticBackdropLabel"}>Reset Password</h2>
                                <button type={"button"} className={"btn-close"} id={"passClose"}
                                        data-bs-dismiss={"modal"} aria-label={"Close"}/>
                            </div>
                            <form id={"passForm"} className={"was-validated"}>
                                <div className={"modal-body"}>
                                    <div className={"form-floating mb-3"}>
                                        <input type={"text"} className={"form-control"} id={"passwordR"}
                                               onKeyUp={() => formCheck("passForm", "passSubmit")}
                                               required={true}/>
                                        <label htmlFor={"passwordR"}>New Password</label>
                                        <div className={"invalid-feedback"}>
                                            Please enter a valid password! 6 Character minimum.
                                        </div>
                                    </div>
                                    <div className={"form-floating mb-3"}>
                                        <input type={"text"} className={"form-control"} id={"passwordCR"}
                                               onKeyUp={() => formCheck("passForm", "passSubmit")} required={true}
                                               pattern={"[ -~]{6,}"}/>
                                        <label htmlFor={"passwordCR"}>Confirm Password</label>
                                        <div className={"invalid-feedback"}>
                                            Password does not match.
                                        </div>
                                    </div>
                                </div>
                                <div className={"modal-footer"}>
                                    <div>
                                        <button type={"button"} className={"btn btn-secondary"}
                                                id={"cancelPassBtn"} data-bs-dismiss={"modal"}>Cancel
                                        </button>
                                        {editRights && higherRole &&
                                            <>
                                                <Button type={"button"} class={"btn btn-primary d-none ms-3"}
                                                        disabled={true}
                                                        spinner={true} text={"Loading"} id={"loadPassSubmit"}/>
                                                <button type={"button"} className={"btn btn-danger ms-3"}
                                                        id={"passSubmit"}
                                                        onClick={() => passSubmit()}>Submit
                                                </button>
                                            </>
                                        }
                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
                <div className={"modal fade"} id={"deleteconfirmmodal"} data-bs-backdrop={"static"}
                     data-bs-keyboard={"false"}
                     tabIndex={-1} aria-labelledby={"staticBackdropLabel"} aria-hidden={"true"}>
                    <div className={"modal-dialog"}>
                        <div className={"modal-content"}>
                            <div className={"modal-header bg-pal"}>
                                <h2 className={"modal-title"} id={"staticBackdropLabel"}>Confirm Delete</h2>
                                <button type={"button"} className={"btn-close"} id={"deleteConfirmClose"}
                                        data-bs-dismiss={"modal"} aria-label={"Close"}/>
                            </div>
                            <div className={"modal-body"}>
                                <p>Are you sure you wish to <span
                                    className={"fw-bold"}>permanently delete {displayName}</span> from the system.
                                    They will no longer have access to the PalCare Portal or Mobile app?</p>
                            </div>
                            <div className={"modal-footer"}>
                                <button type={"button"} className={"btn btn-secondary"} style={{width: "75px"}}
                                        id={"deleteConfirmCancelBtn"} data-bs-dismiss={"modal"}>No
                                </button>
                                {deleteRights && higherRole &&
                                    <>
                                        <Button type={"button"} class={"btn btn-primary d-none ms-3"} disabled={true}
                                                spinner={true}
                                                text={"Loading"} id={"loadDeleteConfirmSubmit"}/>
                                        <button type={"button"} className={"btn btn-primary ms-3"}
                                                id={"deleteConfirmSubmit"}
                                                onClick={() => deletePerform()}>YES
                                        </button>
                                    </>
                                }
                            </div>
                        </div>
                    </div>
                </div>
                <div className={"modal fade"} id={"addnotemodal"} data-bs-backdrop={"static"} data-bs-keyboard={"false"}
                     tabIndex={-1} aria-labelledby={"staticAddNoteBackdropLabel"} aria-hidden={"true"}>
                    <div className={"modal-dialog"}>
                        <div className={"modal-content"}>
                            <div className={"modal-header bg-pal"}>
                                <h2 className={"modal-title text-capitalize"} id={"staticAddNoteBackdropLabel"}>Add
                                    Note</h2>
                                <button type={"button"} className={"btn-close"} id={"addNoteClose"}
                                        data-bs-dismiss={"modal"} aria-label={"Close"}/>
                            </div>
                            <form id={"addNoteForm"} className={"was-validated"}>
                                <div className={"modal-body"}>
                                    {dData?.event_notes_preform
                                        ? <div className={"form-floating d-block"}>
                                            <select className={"form-select form-select my-2"} id={"note"}
                                                    required={true}
                                                    onChange={(e) => {
                                                        if (e.target.value === "0") {
                                                            document.getElementById("freeform").classList.remove("d-none");
                                                            document.getElementById("note-freeform").required = true;
                                                        } else {
                                                            document.getElementById("note-freeform").required = false;
                                                            document.getElementById("freeform").classList.add("d-none");
                                                        }
                                                        formCheck("addNoteForm", "addNoteSubmit");
                                                    }}>
                                                {prenoteOptions}
                                            </select>
                                            <label htmlFor={"note"} className={"form-label"}>Note</label>
                                        </div>
                                        :   <div className={"my-2 form-floating"}>
                                                <textarea className={"form-control pt-5"} id={"note"} required={true}
                                                          style={{height: "125px"}}
                                                          onKeyUp={() => formCheck("addNoteForm", "addNoteSubmit")}>

                                                </textarea>
                                                <label htmlFor={"note"} className={"form-label"}>Note</label>
                                                <div className={"invalid-feedback"}>
                                                    Note required
                                                </div>
                                            </div>
                                    }
                                    <div className={"my-2 form-floating d-none"} id={"freeform"}>
                                        <textarea className={"form-control pt-5"} id={"note-freeform"}
                                                  style={{height: "125px"}}
                                                  onKeyUp={() => formCheck("addNoteForm", "addNoteSubmit")}>
                                        </textarea>
                                        <label htmlFor={"note-freeform"} className={"form-label"}>Note</label>
                                        <div className={"invalid-feedback"}>
                                            Note required
                                        </div>
                                    </div>
                                    {noteTableLength > 0
                                        ? <>
                                            <hr/>
                                            <table className={"table table-sm table-white table-hover"}>
                                                <thead className={isBannerPresent() ? "sticky-table-headX" : "sticky-table-head"}>
                                                {notesTableHeaders}
                                                </thead>
                                                <tbody id={"tBody"}>
                                                {noteTableRows}
                                                </tbody>
                                            </table>
                                        </>
                                        : <></>
                                    }
                                </div>
                                <div className={"modal-footer"}>
                                    <div>
                                        <button type={"button"} className={"btn btn-secondary"} id={"cancelAddNoteBtn"}
                                                data-bs-dismiss={"modal"}>Cancel
                                        </button>
                                        <Button type={"button"} class={"btn btn-primary d-none ms-3"} disabled={true}
                                                spinner={true} text={"Loading"} id={"loadAddNoteSubmit"}/>
                                        <button type={"button"} className={"btn btn-primary ms-3"} id={"addNoteSubmit"}
                                                onClick={() => addNoteSubmit()}>Save
                                        </button>
                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
            <PerPageModal filterSubmit={() => filterSubmit()} tmpPerPage={tmpPerPage}
                                              setTmpPerPage={(sudocode) => setTmpPerPage(sudocode)}
                                              setPage={(sudocode) => setLogPage(sudocode)}/>
            <button
                type={"button"}
                className={"help-corner-button"}
                onClick={() => showModal("helpmodal")}
                title={"Page Help Popup"}
                aria-label={"Open Help"}
            >
                <i className={"fe fe-help-circle help-corner-button-icon"}></i>
            </button>
            <div className={"modal fade help-modal-pop"} id={"helpmodal"} data-bs-backdrop={"static"} data-bs-keyboard={"false"}
                 tabIndex={-1} aria-labelledby={"staticHelpBackdropLabel"} aria-hidden={"true"}>
                <div className={"modal-dialog modal-lg"}>
                    <div className={"modal-content"}>
                        <div className={"modal-header bg-pal"}>
                            <h2 className={"modal-title"} id={"staticHelpBackdropLabel"}>User Profile Page Help</h2>
                            <button type={"button"} className={"btn-close"} data-bs-dismiss={"modal"} aria-label={"Close"}/>
                        </div>
                        <div className={"modal-body"}>
                            <div className={"mb-4"}>
                                <h4 className={"text-primary mb-3"}>
                                    <i className={"fe fe-info me-2"}></i>Getting Started
                                </h4>
                                <p className={"mb-2"}>
                                    This page provides detailed information about a specific user in your system, including basic information, reporting data, and activity logs. You can view user details, edit user information (if you have appropriate permissions), reset passwords, and review user performance metrics.
                                </p>
                            </div>

                            <div className={"mb-4"}>
                                <h5 className={"fw-bold mb-2"}>
                                    <i className={"fe fe-layers me-2 text-primary"}></i>Page Tabs
                                </h5>
                                <p className={"mb-2"}>The page is organized into several tabs:</p>
                                <ul className={"list-unstyled ms-4"}>
                                    <li className={"mb-2"}><strong>Basic Info</strong>: View and edit user details such as display name, email, phone, role, job title, and groups</li>
                                    <li className={"mb-2"}><strong>Reporting</strong>: View charts and statistics about alarms accepted by this user, response times, and event reasons</li>
                                    {iAmHigherAuthority(80, dData?.role_weight) ? <li className={"mb-2"}><strong>User Logs</strong>: View activity logs for this user (available to higher authority users)</li> : <></>}
                                </ul>
                            </div>

                            <div className={"mb-4"}>
                                <h5 className={"fw-bold mb-2"}>
                                    <i className={"fe fe-info me-2 text-primary"}></i>Basic Info Tab
                                </h5>
                                <p className={"mb-2"}>This tab displays the user's basic information:</p>
                                <ul className={"list-unstyled ms-4"}>
                                    <li className={"mb-2"}><strong>Username</strong>: The user's login username (read-only)</li>
                                    <li className={"mb-2"}><strong>Display Name</strong>: The user's display name shown throughout the system (editable with proper permissions)</li>
                                    <li className={"mb-2"}><strong>Email</strong>: The user's email address (editable, required for most roles except Caregiver)</li>
                                    <li className={"mb-2"}><strong>Phone</strong>: The user's phone number (editable)</li>
                                    <li className={"mb-2"}><strong>Role</strong>: The user's role/permission level (editable with proper permissions)</li>
                                    <li className={"mb-2"}><strong>Job Title</strong>: The user's job title, which can be selected from standard titles or entered as a custom title (editable)</li>
                                    <li className={"mb-2"}><strong>Groups</strong>: User groups this user belongs to</li>
                                </ul>
                                <p className={"mb-2"}>Click the "Edit" button to modify user information. Changes require appropriate permissions. You can only edit users who have a lower role weight than your own role.</p>
                            </div>

                            <div className={"mb-4"}>
                                <h5 className={"fw-bold mb-2"}>
                                    <i className={"fe fe-bar-chart-2 me-2 text-primary"}></i>Reporting Tab
                                </h5>
                                <p className={"mb-2"}>This tab provides visual analytics and event data for events accepted by this user:</p>
                                <ul className={"list-unstyled ms-4"}>
                                    <li className={"mb-2"}><strong>Hourly Chart</strong>: Shows alarm frequency by hour of day and average response times for events this user accepted</li>
                                    <li className={"mb-2"}><strong>Weekly Trend Chart</strong>: Displays alarm trends over time for events this user accepted</li>
                                    <li className={"mb-2"}><strong>Event Reason Breakdown</strong>: Table showing alarm reasons, counts, percentages, and average response times for events this user accepted</li>
                                    <li className={"mb-2"}><strong>Recent Events Table</strong>: List of recent alarms accepted by this user with details including event time, response time, device, resident, location, and reason</li>
                                </ul>
                                <p className={"mb-2"}>Click "Run Detailed Report" to generate a comprehensive report for this user's activity.</p>
                            </div>

                            {iAmHigherAuthority(80, dData?.role_weight)
                                ? <div className={"mb-4"}>
                                    <h5 className={"fw-bold mb-2"}>
                                        <i className={"fe fe-file-text me-2 text-primary"}></i>User Logs Tab
                                    </h5>
                                    <p className={"mb-2"}>This tab (available to higher authority users) shows activity logs:</p>
                                    <ul className={"list-unstyled ms-4"}>
                                        <li className={"mb-2"}>View all changes and activities performed by this user</li>
                                        <li className={"mb-2"}>Filter logs by event type and performed by</li>
                                        <li className={"mb-2"}>Sort and paginate through log entries</li>
                                    </ul>
                                </div>
                                : <></>
                            }

                            <div className={"mb-4"}>
                                <h5 className={"fw-bold mb-2"}>
                                    <i className={"fe fe-edit me-2 text-primary"}></i>Editing User Information
                                </h5>
                                <p className={"mb-2"}>To edit user information:</p>
                                <ul className={"list-unstyled ms-4"}>
                                    <li className={"mb-2"}>You must have a higher role weight than the user you're editing</li>
                                    <li className={"mb-2"}>Click the "Edit" button in the Basic Info tab</li>
                                    <li className={"mb-2"}>Modify the fields you wish to change (display name, email, phone, role, job title)</li>
                                    <li className={"mb-2"}>Note: Email is required for most roles, except for Caregiver role</li>
                                    <li className={"mb-2"}>Job title can be selected from standard options (Executive Director, Maintenance Director, Nurse, Caregiver) or entered as "Other" with a custom title</li>
                                    <li className={"mb-2"}>Click "Save" to apply the changes</li>
                                </ul>
                            </div>

                            <div className={"mb-4"}>
                                <h5 className={"fw-bold mb-2"}>
                                    <i className={"fe fe-lock me-2 text-primary"}></i>Resetting Password
                                </h5>
                                <p className={"mb-2"}>To reset a user's password:</p>
                                <ul className={"list-unstyled ms-4"}>
                                    <li className={"mb-2"}>You must have a higher role weight than the user</li>
                                    <li className={"mb-2"}>Click the <strong>"Reset Password"</strong> button in the header</li>
                                    <li className={"mb-2"}>Enter a new password (minimum 6 characters)</li>
                                    <li className={"mb-2"}>Confirm the password and click "Submit"</li>
                                </ul>
                            </div>

                            <div className={"mb-4"}>
                                <h5 className={"fw-bold mb-2"}>
                                    <i className={"fe fe-user-x me-2 text-primary"}></i>Deleting Users
                                </h5>
                                <p className={"mb-2"}>To delete a user:</p>
                                <ul className={"list-unstyled ms-4"}>
                                    <li className={"mb-2"}>You must have a higher role weight than the user</li>
                                    <li className={"mb-2"}>Click the <strong>"Delete"</strong> button in the header</li>
                                    <li className={"mb-2"}>Confirm the deletion in the modal</li>
                                    <li className={"mb-2"}>Deleting a user will permanently remove them from the system and revoke access to the PalCare Portal and Mobile app</li>
                                </ul>
                            </div>

                            <div className={"mb-4"}>
                                <h5 className={"fw-bold mb-2"}>
                                    <i className={"fe fe-edit me-2 text-primary"}></i>Adding Notes to Events
                                </h5>
                                <p className={"mb-2"}>In the Reporting tab, you can add notes to events:</p>
                                <ul className={"list-unstyled ms-4"}>
                                    <li className={"mb-2"}>Click the notes badge on any event row</li>
                                    <li className={"mb-2"}>Select a preformatted note or enter a custom note</li>
                                    <li className={"mb-2"}>View existing notes in the modal</li>
                                    <li className={"mb-2"}>Notes are timestamped and show who created them</li>
                                </ul>
                            </div>

                            <div className={"mb-3"}>
                                <h5 className={"fw-bold mb-2"}>
                                    <i className={"fe fe-settings me-2 text-primary"}></i>Additional Tips
                                </h5>
                                <ul className={"list-unstyled ms-4"}>
                                    <li className={"mb-2"}>Use the collapse/expand icons on card headers to show or hide sections</li>
                                    <li className={"mb-2"}>Charts automatically refresh every 20 minutes with the latest data</li>
                                    <li className={"mb-2"}>Basic information automatically refreshes every 10 minutes</li>
                                    <li className={"mb-2"}>Click on device names, resident names, area names, or event times to navigate to detailed profile pages</li>
                                    <li className={"mb-2"}>Event reason indicators show if a reason has been edited</li>
                                    <li className={"mb-2"}>Use pagination controls at the bottom of tables to navigate through data</li>
                                    <li className={"mb-2"}>You can only edit, reset passwords for, or delete users who have a lower role weight than your own role</li>
                                    <li className={"mb-2"}>Role weight determines permission levels - higher numbers indicate higher authority</li>
                                </ul>
                            </div>
                        </div>
                        <div className={"modal-footer"}>
                            <button type={"button"} className={"btn btn-secondary"} data-bs-dismiss={"modal"}>Close</button>
                        </div>
                    </div>
                </div>
            </div>
            <Suspense fallback={"Loading..."}>
                <ToastContainer position={"bottom-right"} theme={"colored"} newestOnTop={true} pauseOnHover={true}
                                autoClose={10000} closeOnClick={true}/>
            </Suspense>
        </>
    );
};

export default UserProfile;